'use client'

import { useSyncExternalStore } from 'react'

/**
 * Shared service-health subscription — the ONLY poller for /api/health and
 * /api/data-health. Nothing else may fetch them on an interval.
 *
 * Two reasons this is a singleton:
 *
 * 1. <ServiceStatus /> is mounted twice on every page (mobile + desktop
 *    headers, with Tailwind hiding one), so subscriber-count dedup is needed
 *    even within a single tree.
 *
 * 2. Until 2026-08-13 useBackendConnection ran its own independent 30s loop
 *    against the same two endpoints. Combined with this hook's 60s loop that
 *    was 180 requests/hour to each endpoint per open tab — and AFD logs showed
 *    the two health endpoints accounting for 46,688 calls in 7 days, roughly
 *    70% of all Crucible API traffic. useBackendConnection now derives from
 *    this module instead of polling; keep it that way.
 *
 * The probe (timeout, no-store, error strings) came from useBackendConnection,
 * which had the more careful implementation of the two.
 */

export interface ServiceHealth {
  ai: boolean
  data: boolean
  /** Human-readable reason a service is down, or null when it is up. */
  aiError?: string | null
  dataError?: string | null
}

let currentHealth: ServiceHealth | null = null
const subscribers = new Set<() => void>()
let intervalId: ReturnType<typeof setInterval> | null = null
let visibilityListener: (() => void) | null = null
let inFlight = false
let prevKey = ''

const POLL_INTERVAL_MS = 60_000
const PROBE_TIMEOUT_MS = 6_000

async function probe(
  path: string,
  key: 'ai' | 'data',
  serviceName: string,
): Promise<{ up: boolean; error: string | null }> {
  try {
    const response = await fetch(path, {
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
      cache: 'no-store',
    })
    if (!response.ok) {
      return { up: false, error: `${serviceName} health check returned ${response.status}` }
    }
    const json = await response.json()
    const up = json?.[key] === true
    return { up, error: up ? null : `${serviceName} service unreachable` }
  } catch {
    return { up: false, error: `${serviceName} service unreachable` }
  }
}

async function check() {
  if (inFlight) return // collapse overlapping fires
  inFlight = true
  try {
    const [ai, data] = await Promise.all([
      probe('/api/health', 'ai', 'AI'),
      probe('/api/data-health', 'data', 'Data'),
    ])
    publish({ ai: ai.up, data: data.up, aiError: ai.error, dataError: data.error })
  } catch {
    publish({
      ai: false,
      data: false,
      aiError: 'AI service unreachable',
      dataError: 'Data service unreachable',
    })
  } finally {
    inFlight = false
  }
}

/**
 * Notify subscribers only when something actually changed. Without this the
 * poll re-renders every consumer on a timer even in the steady state, and
 * useBackendConnection sits high enough in the tree (workspace-context) that
 * a spurious re-render is expensive.
 */
function publish(next: ServiceHealth) {
  const nextKey = `${next.ai}:${next.aiError}|${next.data}:${next.dataError}`
  const isFirst = currentHealth === null
  currentHealth = next
  if (!isFirst && nextKey === prevKey) return
  prevKey = nextKey
  subscribers.forEach((notify) => notify())
}

function startLoop() {
  check()
  // Skip the tick while the tab is backgrounded and catch up immediately on
  // return. A dormant tab was polling at the same rate as an active one, and
  // several open Crucible conversation tabs is a normal usage pattern.
  intervalId = setInterval(() => {
    if (document.visibilityState === 'visible') check()
  }, POLL_INTERVAL_MS)
  visibilityListener = () => {
    if (document.visibilityState === 'visible') check()
  }
  document.addEventListener('visibilitychange', visibilityListener)
}

function stopLoop() {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener)
    visibilityListener = null
  }
}

function subscribe(onStoreChange: () => void): () => void {
  subscribers.add(onStoreChange)
  // First subscriber starts the polling loop; later subscribers piggyback.
  if (intervalId === null) startLoop()
  return () => {
    subscribers.delete(onStoreChange)
    // Last subscriber leaving stops the loop. New subscribers will revive it.
    if (subscribers.size === 0) stopLoop()
  }
}

/**
 * This module already *was* an external store — a module-level value, a
 * subscriber set and a notify function. It was read through useState plus an
 * effect that re-pushed the cached value to any subscriber joining a loop
 * already in flight, which is a synchronous set-state inside an effect
 * (react-hooks/set-state-in-effect) and, worse, a copy: a second
 * <ServiceStatus /> mounting mid-poll rendered `null` once before the effect
 * caught it up. useSyncExternalStore reads the live value during render
 * instead, so a late subscriber is current on its first frame and the
 * catch-up push is not needed at all.
 *
 * The server snapshot is null — there is nothing to report before the first
 * poll, which is what the initial state said too.
 */
export function useServiceHealth(): ServiceHealth | null {
  return useSyncExternalStore(subscribe, () => currentHealth, () => null)
}
